import type { KommentarArt, KommentarArten } from './kommentararten';
import { addFrac, frac, fromNumber, type Fraction } from './points';
import type { CommentBox, Station, Task, TaskId } from './types';

/**
 * Bewertungsstand einer Station, so wie ihn der Prüfer erzeugt.
 *
 * Getrennt von den Aufgabendefinitionen, weil das eine von der Prüfung kommt und das andere
 * je Prüfling neu entsteht — beim Wechsel des Prüflings wird nur dieser Teil ausgetauscht.
 */
export interface Assessment {
  /** Angehakte Zeilen je Checklisten-Aufgabe. */
  checked: Record<TaskId, boolean[]>;
  /** Gewählte Stufe je Kriterium, Schlüssel `<taskId>#<index>`. */
  criteria: Record<string, number>;
  /** Von Hand gesetzte Punktzahl; bei globalen Aufgaben der einzige Weg zur Punktzahl. */
  override: Record<TaskId, number | null>;
  /** Markierte Wortgruppen der globalen Aufgabe, Schlüssel `<taskId>#<wortIndex>`. */
  pickedWords: Record<string, boolean>;
  comments: Record<TaskId, CommentBox>;
}

export const emptyAssessment = (): Assessment => ({
  checked: {},
  criteria: {},
  override: {},
  pickedWords: {},
  comments: {},
});

export const criterionKey = (taskId: TaskId, index: number) => taskId + '#' + index;
export const wordKey = (taskId: TaskId, index: number) => taskId + '#' + index;

/** Höchstpunktzahl der Aufgabe. */
export function taskMax(task: Task): Fraction {
  switch (task.scoring.kind) {
    case 'global':
      return fromNumber(task.scoring.max);
    case 'checklist':
      return task.scoring.items.reduce((sum, item) => addFrac(sum, fromNumber(item.points)), frac(0));
    case 'criteria':
      return task.scoring.criteria.reduce(
        (sum, c) => addFrac(sum, fromNumber(Math.max(...c.options.map((o) => o.points)))),
        frac(0),
      );
  }
}

/**
 * Punktzahl, die sich aus den Eingaben ergibt — ohne den Handeingriff.
 * Globale Aufgaben haben keine ableitbare Punktzahl, dort ist die Grundlage 0.
 */
export function taskBaseScore(task: Task, a: Assessment): Fraction {
  switch (task.scoring.kind) {
    case 'global':
      return frac(0);
    case 'checklist': {
      const checked = a.checked[task.id] ?? [];
      return task.scoring.items.reduce(
        (sum, item, i) => (checked[i] ? addFrac(sum, fromNumber(item.points)) : sum),
        frac(0),
      );
    }
    case 'criteria':
      return task.scoring.criteria.reduce((sum, c, i) => {
        const chosen = a.criteria[criterionKey(task.id, i)];
        return chosen == null ? sum : addFrac(sum, fromNumber(c.options[chosen].points));
      }, frac(0));
  }
}

/** Die geltende Punktzahl der Aufgabe: Handeingriff schlägt Berechnung. */
export function taskScore(task: Task, a: Assessment): Fraction {
  const override = a.override[task.id];
  return override == null ? taskBaseScore(task, a) : fromNumber(override);
}

/**
 * Ob die Punktzahl von Hand gesetzt wurde. Bei globalen Aufgaben ist das der Normalweg
 * und wird deshalb nicht als Eingriff angezeigt.
 */
export function isOverridden(task: Task, a: Assessment): boolean {
  return task.scoring.kind !== 'global' && a.override[task.id] != null;
}

export function totalScore(tasks: Task[], a: Assessment): Fraction {
  return tasks.reduce((sum, t) => addFrac(sum, taskScore(t, a)), frac(0));
}

export function totalMax(tasks: Task[]): Fraction {
  return tasks.reduce((sum, t) => addFrac(sum, taskMax(t)), frac(0));
}

/** Ob die Aufgabe als bewertet gilt — Grundlage der Fortschrittszeile im Kopf. */
export function isTaskDone(task: Task, a: Assessment): boolean {
  switch (task.scoring.kind) {
    case 'global':
      return a.override[task.id] != null;
    case 'checklist':
      return true;
    case 'criteria':
      return task.scoring.criteria.every(
        (_, i) => a.criteria[criterionKey(task.id, i)] != null,
      );
  }
}

/**
 * Wie viele Aufgaben einer Station noch offen sind.
 *
 * ⚠️ **Eine Checkliste gilt immer als bewertet** (`isTaskDone`): ein leeres Kästchen ist dort
 * eine Aussage — „hat er nicht getan" —, kein fehlender Wert. Offen sind deshalb nur globale
 * Aufgaben ohne gesetzte Punktzahl und Kriterienleisten mit einer ungewählten Zeile.
 *
 * Gebraucht in der Prüflingsliste (`docs/oberflaeche-luecken.md`, Punkt 2): der Prüfer sieht
 * dort sonst nur „läuft" und muss den Bogen öffnen, um zu wissen, ob noch etwas fehlt.
 */
export function offeneAufgaben(tasks: Task[], a: Assessment): number {
  return tasks.reduce((offen, t) => offen + (isTaskDone(t, a) ? 0 : 1), 0);
}

/**
 * Ob der Prüfer an dieser Station überhaupt etwas hinterlassen hat.
 *
 * ⚠️ **Nicht dasselbe wie `isTaskDone`.** Das sagt eine Checkliste *immer* als bewertet an —
 * ein leeres Kästchen ist dort die Aussage „hat er nicht getan". Hier geht es um die andere
 * Frage: ist der Bogen angefasst worden? Ein unberührter Bogen hat nichts zu belegen.
 *
 * ⚠️ **Die Station muss mitkommen, obwohl `Assessment` sie nicht kennt.** Der Stand gilt je
 * **Prüfling** und trägt die Aufgaben aller Stationen nebeneinander. Ohne die Eingrenzung auf
 * `station.tasks` gälte ein an Station 1 bewerteter Prüfling auch an Station 2 als berührt —
 * und der Wechsel dorthin erzeugte ein Beweisbild einer Station, an der nichts geschah.
 *
 * Gebraucht vom Bogenabzug beim Wechsel (`store/useAppStore.ts`): dort entscheidet diese
 * Funktion, ob überhaupt aufgenommen wird. Beim Abgeben entscheidet sie **nicht** mit — dort
 * entsteht der Abzug immer (Vorgabe des Nutzers, 2026-09-17).
 */
export function stationBeruehrt(a: Assessment, station: Station): boolean {
  return station.tasks.some((task) => {
    if ((a.checked[task.id] ?? []).some(Boolean)) return true;
    // `!= null`, nicht `!!`: Stufe 0 heisst „nicht gezeigt" und ist eine getroffene Wahl.
    if (task.scoring.kind === 'criteria'
      && task.scoring.criteria.some((_, i) => a.criteria[criterionKey(task.id, i)] != null)) {
      return true;
    }
    if (a.override[task.id] != null) return true;
    // Eine abgewählte Wortgruppe bleibt als `false` stehen; nur eine gesetzte zählt.
    if (task.scoring.kind === 'global'
      && Object.keys(a.pickedWords).some((k) => a.pickedWords[k] && k.startsWith(task.id + '#'))) {
      return true;
    }
    // Ohne `arten`: die **wahre** Zahl. Was die Oberfläche gerade zeigt, ändert nichts daran,
    // ob etwas hinterlassen wurde.
    return commentCount(a.comments[task.id]) > 0;
  });
}

/*
 * Die Schreibweise von Punktzahlen steht **nicht** hier. Das Dezimalzeichen ist eine
 * Eigenschaft der Oberflächensprache und gehört deshalb in den Sprachkatalog —
 * siehe `usePoints()` und `formatPointsIn()` in `src/i18n`.
 */

/**
 * Wie viele Kommentare an einer Aufgabe oder Station hängen.
 *
 * Text und Handschrift zählen als **eines** (es gibt je nur eines davon), Audio, Fotos und
 * Videos stückweise.
 *
 * ⚠️ **`arten` ist optional, und das mit Absicht.** Ohne Angabe zählt die Funktion alles —
 * die Rundreise-Tests und der Absendedialog brauchen die **wahre** Zahl, dort darf eine
 * Oberflächeneinstellung nichts verschweigen. Mit Angabe zählt sie nur, was die Oberfläche
 * auch zeigt (`*_comments`, s. `domain/kommentararten.ts`): sonst stünde an der Kachel
 * „6 Kommentare", und beim Öffnen wäre das Blatt leer. Eine Zahl, die auf nichts zeigt, ist
 * schlimmer als keine.
 */
export function commentCount(box: CommentBox | undefined, arten?: KommentarArten): number {
  if (!box) return 0;
  const zaehlt = (art: KommentarArt) => arten === undefined || arten[art];
  return (
    (zaehlt('text') && box.text.trim() ? 1 : 0) +
    (zaehlt('audio') ? box.audios.length : 0) +
    (zaehlt('photo') ? box.photos.length : 0) +
    (zaehlt('video') ? box.videos.length : 0) +
    (zaehlt('handwriting') && box.handwriting.length > 0 ? 1 : 0)
  );
}
